import { MultiSelectOption } from '@/models/MultiSelectOption';
import { TailwindWidthClass } from '@/models/TailwindWIdthClass';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, ReactNode, type FC } from "react";

interface DropdownMenuProps {
    options: MultiSelectOption[],
    onSelected: (item: MultiSelectOption) => void,
    childClassName?: string,
    buttonClassName?: string,
    className?: string,
    cyName?: string,
    width?: TailwindWidthClass,
    children: ReactNode,
    direction?: 'top' | 'bottom'
}

const DropdownSelector: FC<DropdownMenuProps> = ({ options, onSelected, childClassName, buttonClassName, className, cyName, width = 'w-56', children, direction = 'bottom' }) => {
    const getMenuPositionClasses = () => {
        if (direction === 'top') {
            return 'bottom-full mb-2 origin-bottom-right';
        }
        return 'mt-2 origin-top-right';
    };

    const getTransformOrigin = () => {
        if (direction === 'top') {
            return {
                enterFrom: 'transform opacity-0 scale-95 translate-y-1',
                enterTo: 'transform opacity-100 scale-100 translate-y-0',
                leaveFrom: 'transform opacity-100 scale-100 translate-y-0',
                leaveTo: 'transform opacity-0 scale-95 translate-y-1'
            };
        }
        return {
            enterFrom: 'transform opacity-0 scale-95 -translate-y-1',
            enterTo: 'transform opacity-100 scale-100 translate-y-0',
            leaveFrom: 'transform opacity-100 scale-100 translate-y-0',
            leaveTo: 'transform opacity-0 scale-95 -translate-y-1'
        };
    };

    const transformClasses = getTransformOrigin();

    return <div className={className}>
        <Menu as="div" className="relative inline-block" id="exportContainer">
            {({ open }) => (
                <>
                    <Menu.Button data-cy={cyName} className={buttonClassName ? buttonClassName : "flex flex-row items-center w-full justify-between pt-1 cursor-pointer"}>
                        <div className='cursor-pointer'>
                            {children}
                        </div>
                        <FontAwesomeIcon className={childClassName ? childClassName : `flex pl-1 text-xs`} icon={open ? faChevronUp : faChevronDown} />
                    </Menu.Button>
                    <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom={transformClasses.enterFrom}
                        enterTo={transformClasses.enterTo}
                        leave="transition ease-in duration-75"
                        leaveFrom={transformClasses.leaveFrom}
                        leaveTo={transformClasses.leaveTo}>
                        <Menu.Items style={{ right: '-2rem' }} className={`absolute ${getMenuPositionClasses()} z-50 ${width} max-w-56 rounded-md bg-white text-black shadow-xl ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none`}>
                            {options?.map((item, i) =>
                                <Menu.Item key={item.value}>
                                    {({ active }) => (
                                        <button type='button' onClick={() => onSelected(item)}
                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} flex w-full items-center px-4 py-2 text-sm hover:bg-gray-50 transition-colors duration-150`}
                                            data-cy={`MenuItem-${i}`}>
                                            {item.iconUrl && <img className="h-5 mr-2" src={item.iconUrl} alt={item.label} />}
                                            <span className="truncate">{item?.label}</span>
                                        </button>
                                    )}
                                </Menu.Item>)}
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    </div>
}

export default DropdownSelector;